import { useEffect, useState } from 'react';
import { ocenaRyzyka as riskApi } from '@/api/endpoints';
import { fmtBytes } from '@/lib/uploadLimits';
import { Icon } from '@/components/Icon';

/**
 * Podgląd zapisanej mapy analizy ryzyka — proweniencji obszaru zasilania (art. 4f).
 * Renderuje kafelek z miniaturą i metadaną, gdy ocena ma zapisaną mapę; nic, gdy brak.
 * Widoczny również bez uprawnień do edycji (proweniencja jest do wglądu recenzenta).
 *
 * `refreshKey` wymusza ponowny odczyt po wgraniu nowej mapy — bumpowany przez rodzica
 * po zapisie z georeferencera; służy też jako cache-bust na URL obrazu (stały klucz w MinIO
 * nie zmienia adresu, więc bez tego przeglądarka pokazałaby starą mapę z cache).
 */
export function CatchmentMapPreview({ raId, refreshKey = 0, canEdit = false }: { raId: number; refreshKey?: number; canEdit?: boolean }) {
  const [info, setInfo] = useState<{ filename: string; contentType: string; sizeBytes: number } | null>(null);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let alive = true;
    riskApi.catchmentMapInfo(raId)
      .then(r => { if (alive) setInfo(r); })
      .catch(() => { if (alive) setInfo(null); });
    return () => { alive = false; };
  }, [raId, refreshKey]);

  const usun = () => {
    if (!window.confirm('Usunąć zapisaną mapę analizy ryzyka? Obszar zasilania (poligon) pozostanie bez zmian.')) return;
    setBusy(true);
    riskApi.deleteCatchmentMap(raId)
      .then(() => { setInfo(null); setOpen(false); })
      .catch(() => { /* zostaw kafelek — brak zmiany */ })
      .finally(() => setBusy(false));
  };

  if (!info) return null;
  const src = `${riskApi.catchmentMapUrl(raId)}?v=${refreshKey}`;

  return (
    <div className="card"><div className="card-body">
      <div className="flex items-center gap-2 mb-2">
        <Icon name="map" className="w-3.5 h-3.5 text-ink-400 shrink-0" />
        <span className="text-[13px] font-medium text-ink-800">Mapa analizy ryzyka (proweniencja obszaru)</span>
      </div>
      <div className="flex items-start gap-3 flex-wrap">
        <button type="button" onClick={() => setOpen(true)}
          className="shrink-0 border border-ink-100 rounded overflow-hidden hover:border-water-400 transition"
          title="Powiększ">
          <img src={src} alt="Mapa analizy ryzyka" className="block w-40 h-28 object-cover bg-ink-50" />
        </button>
        <div className="min-w-0 text-[12px] text-ink-500 grid gap-1">
          <div className="text-ink-700 font-medium truncate max-w-[240px]" title={info.filename}>{info.filename}</div>
          <div>{fmtBytes(info.sizeBytes)} · {info.contentType}</div>
          <div className="mt-1 flex items-center gap-1 flex-wrap">
            <a href={riskApi.catchmentMapUrl(raId)} target="_blank" rel="noreferrer" className="btn btn-ghost btn-sm">
              <Icon name="download" className="w-3.5 h-3.5" /> Otwórz / pobierz
            </a>
            {canEdit && (
              <button type="button" onClick={usun} disabled={busy} className="btn btn-ghost btn-sm text-red-500">
                <Icon name="x" className="w-3.5 h-3.5" /> Usuń mapę
              </button>
            )}
          </div>
          <div className="text-ink-400">Obraz, z którego odczytano obszar zasilania (georeferencja).</div>
        </div>
      </div>

      {open && (
        <div className="fixed inset-0 z-50 bg-black/70 grid place-items-center p-4" onClick={() => setOpen(false)}>
          <img src={src} alt="Mapa analizy ryzyka" onClick={e => e.stopPropagation()}
            className="max-w-[92vw] max-h-[90vh] object-contain rounded shadow-xl" />
        </div>
      )}
    </div></div>
  );
}
